/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook useBatchPostPayment
 */
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import api from 'client/index';
import { JsonTree } from '@react-awesome-query-builder/antd';
import { useCustomAttributesStoreValues } from 'store/customAttributes';
import AppEvents from '@veritable/common/utils/appEvents';
import { buildCriteria } from 'utils/advancedFilterUtils';
import { getClaimAdfFieldMap } from 'utils/adfUtils';
import QUERY_KEYS from 'app-constants/queryKeys';
import { CUSTOM_ATTRIBUTE_ENTITY_TYPE } from 'app-constants/admin/customAttribute';

interface IProps {
	requestParams: PatientPayment.IBatchPostPaymentRequest;
}
const useBatchPostPayment = (): UseMutationResult<unknown, IAxiosError, IProps, unknown> => {
	const queryClient = useQueryClient();
	const { customAttributes } = useCustomAttributesStoreValues();
	const claimAttributes = customAttributes?.[CUSTOM_ATTRIBUTE_ENTITY_TYPE.claim];
	return useMutation({
		mutationFn: async ({ requestParams }) => {
			const { data } = await api.FHIRClaimApi.createClaimBatchPostPayment({
				...requestParams,
				batchClaimPostPaymentRequest: {
					...requestParams.batchClaimPostPaymentRequest,
					claims: {
						...(buildCriteria(
							(requestParams.batchClaimPostPaymentRequest.claims?.criteria || {}) as JsonTree,
							getClaimAdfFieldMap(claimAttributes),
						) || {}),
					},
				},
			});
			return data;
		},
		onSuccess: () => {
			AppEvents.publishApiSuccess();
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.claimStatusRequests] });
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.eraDetails] });
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.batchPostPaymentHistory] });
		},
		onError: (error) => {
			AppEvents.publishApiError({ error });
		},
	});
};

export default useBatchPostPayment;
